import { LoadingButton } from '@mui/lab';
import { Button, Dialog, Stack, Typography } from '@mui/material';
import { TextField } from 'mui-rff';
import { composeValidators, Validator } from '@src/design-system/forms/validators';
import {
  UpdateItineraryDescriptionDocument,
  BackfillItineraryLocationsDocument,
  UserRole,
  TimelineDocument,
} from '@flashpack/graphql';
import { FC, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { ServerValidationErrors } from '@src/design-system/forms/ServerValidationErrors';
import { ApolloError, useMutation } from '@apollo/client';
import { FORM_ERROR } from 'final-form';
import { useToast } from '@src/shared/toast/useToast';
import { useSafeMutation } from '@src/shared/useSafeMutation';
import { CheckboxFormField } from 'design-system/src/checkbox/CheckboxFormField';
import { FormControlLabel } from 'design-system';
import { Protected } from '@src/authentication/Protected';
import { ConfirmationDialog } from '@src/design-system/dialogs/ConfirmationDialog';

interface PropTypes {
  itineraryId: string;
  open: boolean;
  description: string;
  isVisibleOnWebsite: boolean;
  isAlternativeWebsiteItinerary: boolean;
  onClose: () => void;
}

type FormValues = {
  description: string;
  isVisibleOnWebsite: boolean;
  isAlternativeWebsiteItinerary: boolean;
};

export const EditItineraryDialog: FC<PropTypes> = ({
  open,
  onClose,
  itineraryId,
  description,
  isVisibleOnWebsite,
  isAlternativeWebsiteItinerary,
}) => {
  const [updateItineraryDescription] = useMutation(UpdateItineraryDescriptionDocument);
  const [backfillLocations, { loading: backfilling }] = useMutation(
    BackfillItineraryLocationsDocument,
  );
  const { success, error } = useToast();
  const { safeMutation } = useSafeMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runBackfill = async () => {
    await safeMutation(
      backfillLocations({
        variables: { itineraryId },
        refetchQueries: [TimelineDocument],
      }),
      {
        onSuccess: () => success('Locations updated'),
        onError: () => error('Unable to update locations'),
      },
    );
  };

  const onSubmit = async ({
    description,
    isVisibleOnWebsite,
    isAlternativeWebsiteItinerary,
  }: FormValues) => {
    await safeMutation(
      updateItineraryDescription({
        variables: {
          input: {
            id: itineraryId,
            description,
            isVisibleOnWebsite,
            isAlternativeWebsiteItinerary,
          },
        },
      }),
      {
        onSuccess: () => {
          success('Updated itinerary description');
          onClose();
        },
        onError: (err: unknown) => {
          // Get the first error message or fall back to default message
          const apolloError = err as ApolloError;
          const errorMessage =
            apolloError.graphQLErrors[0]?.message ||
            'Unable to update itinerary description';
          error(errorMessage);
          onClose();
        },
      },
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Stack m={7} gap={5}>
          <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={{
              description,
              isVisibleOnWebsite,
              isAlternativeWebsiteItinerary,
            }}
            render={({ handleSubmit, submitting, dirtySinceLastSubmit, submitError }) => {
              return (
                <form
                  onSubmit={(values) => {
                    try {
                      void handleSubmit(values);
                    } catch (e) {
                      return {
                        [FORM_ERROR]: e,
                      };
                    }
                  }}
                >
                  <Stack gap={1} spacing={3} alignItems="flex-start">
                    <Typography variant="subHeader">Edit {description}</Typography>

                    <TextField
                      fieldProps={{
                        validate: composeValidators(Validator.required),
                      }}
                      autoFocus
                      name="description"
                      data-testid="edit-itinerary-description-input"
                      label="Description"
                    />
                    <Protected roles={[UserRole.Flashpack]}>
                      <FormControlLabel
                        label="Show on website inclusions list"
                        checked={!!isVisibleOnWebsite}
                        control={
                          <Field
                            name="isVisibleOnWebsite"
                            type="checkbox"
                            component={CheckboxFormField}
                            size="small"
                          />
                        }
                      />
                      <FormControlLabel
                        label="Use alternative website itinerary"
                        checked={!!isAlternativeWebsiteItinerary}
                        control={
                          <Field
                            name="isAlternativeWebsiteItinerary"
                            type="checkbox"
                            component={CheckboxFormField}
                            size="small"
                          />
                        }
                      />
                    </Protected>
                    {!dirtySinceLastSubmit && (
                      <ServerValidationErrors error={submitError as ApolloError} />
                    )}
                    <Stack direction="row" gap={3} justifyContent="flex-end">
                      <Button variant="outlined" onClick={onClose}>
                        Cancel
                      </Button>
                      <Protected roles={[UserRole.Flashpack]}>
                        <LoadingButton
                          loading={backfilling}
                          onClick={() => setConfirmOpen(true)}
                          variant="outlined"
                        >
                          Auto Fill Locations
                        </LoadingButton>
                      </Protected>
                      <LoadingButton
                        loading={submitting}
                        type="submit"
                        variant="contained"
                        data-testid="edit-itinerary-submit"
                      >
                        Save
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </form>
              );
            }}
          />
        </Stack>
      </Dialog>
      <ConfirmationDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          void runBackfill();
        }}
        title="Overwrite existing locations?"
        confirmLabel="Apply"
        cancelLabel="Cancel"
        maxWidth="sm"
      >
        <Typography sx={{ mb: 5 }} variant="bodyPara">
          This will overwrite any locations already saved for this itinerary.
        </Typography>
      </ConfirmationDialog>
    </>
  );
};
